import React, { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiFilter, FiX } from 'react-icons/fi';
import { EMPTY_DRIVER_FILTERS } from '../hooks/useCarTows';

const SELECTS = {
    state: { label: 'حالة الحساب', options: [['active', 'نشط'], ['inactive', 'غير نشط'], ['deleted', 'محذوف']] },
    verificationStatus: { label: 'التوثيق', options: [['approved', 'موثق'], ['pending', 'قيد المراجعة'], ['rejected', 'مرفوض'], ['didnt-apply', 'لم يقدم طلب']] },
    vechicleType: { label: 'نوع المركبة', options: [['ساحبة', 'ساحبة'], ['رافعة', 'رافعة']] },
    vechicleSubType: { label: 'نوع الساحبة', options: [['عادية', 'عادية'], ['ركاردو', 'ركاردو'], ['هيدرولكية', 'هيدرولكية'], ['unset', 'لم يحدد بعد']] },
    subscription: { label: 'الاشتراك', options: [['active', 'مشترك حالياً'], ['none', 'بدون اشتراك'], ['scheduled', 'لديه باقة مجدولة']] },
    debt: { label: 'الرصيد', options: [['debt', 'عليه مستحقات'], ['exceeded', 'تجاوز حد الدين'], ['positive', 'رصيد موجب'], ['zero', 'رصيد صفر']] },
    activityLevel: { label: 'مستوى النشاط', options: [['excellent', 'ممتاز'], ['good', 'جيد'], ['normal', 'عادي'], ['bad', 'ضعيف']] },
    ban: { label: 'الحظر', options: [['banned', 'محظور'], ['temp', 'موقوف مؤقتاً'], ['none', 'غير محظور']] },
    payment: { label: 'طرق الدفع', options: [['card', 'يقبل البطاقة'], ['transfer', 'يقبل التحويل'], ['cash-only', 'نقداً فقط']] },
    updateRequestStatus: { label: 'طلب تحديث البيانات', options: [['pending', 'بانتظار المراجعة']] },
};

const RANGES = [
    { label: 'نقاط النشاط', min: 'minActivityPoints', max: 'maxActivityPoints' },
    { label: 'عدد الرحلات', min: 'minTrips', max: 'maxTrips' },
    { label: 'الرحلات الملغاة', min: 'minCancelled', max: 'maxCancelled' },
    { label: 'الإنذارات', min: 'minWarnings', max: 'maxWarnings' },
    { label: 'الرصيد (د.ل)', min: 'minBalance', max: 'maxBalance' },
];

const JOINED = { label: 'تاريخ الانضمام', min: 'joinedFrom', max: 'joinedTo' };

const SORT_OPTIONS = [
    ['newest', 'الأحدث انضماماً'],
    ['oldest', 'الأقدم انضماماً'],
    ['trips-desc', 'الأكثر رحلات'],
    ['points-desc', 'الأعلى نقاط نشاط'],
    ['balance-asc', 'الأكثر مديونية'],
    ['balance-desc', 'الأعلى رصيداً'],
    ['cancelled-desc', 'الأكثر إلغاءً للرحلات'],
];

const PRIMARY_SELECTS = ['state', 'verificationStatus', 'vechicleType', 'vechicleSubType', 'subscription', 'debt'];
const ADVANCED_GROUPS = [
    { title: 'النشاط والأداء', selects: ['activityLevel'], ranges: ['minActivityPoints', 'minTrips', 'minCancelled'] },
    { title: 'الحساب', selects: ['ban', 'payment', 'updateRequestStatus'], ranges: ['minWarnings'] },
    { title: 'المالية والتاريخ', selects: [], ranges: ['minBalance'], joined: true },
];
const ADVANCED_KEYS = [
    'activityLevel', 'ban', 'payment', 'updateRequestStatus',
    'minActivityPoints', 'maxActivityPoints', 'minTrips', 'maxTrips', 'minCancelled', 'maxCancelled',
    'minWarnings', 'maxWarnings', 'minBalance', 'maxBalance', 'joinedFrom', 'joinedTo',
];

const isSet = (value) => String(value ?? '').trim() !== '';
const rangeByMin = (minKey) => RANGES.find((r) => r.min === minKey);
const optionLabel = (key, value) => SELECTS[key]?.options.find(([v]) => v === value)?.[1] || value;
const rangeText = (min, max) => {
    if (isSet(min) && isSet(max)) return `${min} - ${max}`;
    return isSet(min) ? `من ${min}` : `حتى ${max}`;
};

// Human-readable chips for the filters that are currently applied to the list.
const buildChips = (applied) => {
    const chips = [];
    if (isSet(applied.city)) chips.push({ id: 'city', text: `المدينة: ${applied.city}`, clear: { city: '', area: '' } });
    if (isSet(applied.area)) chips.push({ id: 'area', text: `المنطقة: ${applied.area}`, clear: { area: '' } });
    Object.keys(SELECTS).forEach((key) => {
        if (isSet(applied[key])) {
            chips.push({ id: key, text: `${SELECTS[key].label}: ${optionLabel(key, applied[key])}`, clear: { [key]: '' } });
        }
    });
    [...RANGES, JOINED].forEach(({ label, min, max }) => {
        if (isSet(applied[min]) || isSet(applied[max])) {
            chips.push({ id: min, text: `${label}: ${rangeText(applied[min], applied[max])}`, clear: { [min]: '', [max]: '' } });
        }
    });
    return chips;
};

const DriverFiltersPanel = ({ applied, locations, onApply, onClear }) => {
    const [draft, setDraft] = useState({ ...EMPTY_DRIVER_FILTERS, ...applied });
    const [showAdvanced, setShowAdvanced] = useState(() => ADVANCED_KEYS.some((k) => isSet(applied[k])));

    useEffect(() => {
        setDraft({ ...EMPTY_DRIVER_FILTERS, ...applied });
    }, [applied]);

    const cityOptions = useMemo(
        () => Array.from(new Set((locations || []).map((loc) => loc.city).filter(Boolean)))
            .sort((a, b) => String(a).localeCompare(String(b), 'ar', { sensitivity: 'base' })),
        [locations]
    );
    const areaOptions = useMemo(() => {
        const match = (locations || []).find((loc) => (loc.city || '').toLowerCase() === (draft.city || '').toLowerCase());
        return (match?.areas || [])
            .map((a) => (typeof a === 'string' ? a : a?.name))
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b), 'ar', { sensitivity: 'base' }));
    }, [locations, draft.city]);

    const chips = useMemo(() => buildChips(applied), [applied]);
    const advancedCount = ADVANCED_KEYS.filter((k) => isSet(draft[k])).length;
    const isDirty = Object.keys(EMPTY_DRIVER_FILTERS).some((k) => String(draft[k] ?? '') !== String(applied[k] ?? ''));

    const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const submit = (e) => {
        e.preventDefault();
        onApply(draft);
    };

    const renderSelect = (key) => (
        <label key={key} className="filter-field">
            <span>{SELECTS[key].label}</span>
            <select value={draft[key]} onChange={(e) => set(key, e.target.value)}>
                <option value="">الكل</option>
                {SELECTS[key].options.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                ))}
            </select>
        </label>
    );

    const renderRange = ({ label, min, max }, inputType = 'number') => (
        <div key={min} className="filter-field range">
            <span>{label}</span>
            <div className="range-inputs">
                <input type={inputType} value={draft[min]} placeholder="من" onChange={(e) => set(min, e.target.value)} />
                <span className="range-sep">—</span>
                <input type={inputType} value={draft[max]} placeholder="إلى" onChange={(e) => set(max, e.target.value)} />
            </div>
        </div>
    );

    return (
        <form className="driver-filters" onSubmit={submit}>
            <div className="driver-filters__top">
                <h3><FiFilter /> تصفية السائقين</h3>
                <label className="sort-field">
                    <span>ترتيب حسب</span>
                    <select
                        value={draft.sortBy}
                        onChange={(e) => {
                            set('sortBy', e.target.value);
                            onApply({ ...applied, sortBy: e.target.value });
                        }}
                    >
                        {SORT_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="driver-filters__grid">
                <label className="filter-field">
                    <span>المدينة</span>
                    <select value={draft.city} onChange={(e) => setDraft((prev) => ({ ...prev, city: e.target.value, area: '' }))}>
                        <option value="">الكل</option>
                        {cityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
                    </select>
                </label>
                <label className="filter-field">
                    <span>المنطقة</span>
                    <select value={draft.area} onChange={(e) => set('area', e.target.value)} disabled={!draft.city}>
                        <option value="">{draft.city ? 'الكل' : 'اختر المدينة أولاً'}</option>
                        {areaOptions.map((area) => <option key={area} value={area}>{area}</option>)}
                    </select>
                </label>
                {PRIMARY_SELECTS.map(renderSelect)}
            </div>

            <button type="button" className={`advanced-toggle ${showAdvanced ? 'open' : ''}`} onClick={() => setShowAdvanced((v) => !v)}>
                <span>فلاتر متقدمة</span>
                {advancedCount > 0 && <span className="count">{advancedCount}</span>}
                <FiChevronDown />
            </button>

            {showAdvanced && (
                <div className="driver-filters__advanced">
                    {ADVANCED_GROUPS.map((group) => (
                        <fieldset key={group.title}>
                            <legend>{group.title}</legend>
                            <div className="driver-filters__grid">
                                {group.selects.map(renderSelect)}
                                {group.ranges.map((minKey) => renderRange(rangeByMin(minKey)))}
                                {group.joined && renderRange(JOINED, 'date')}
                            </div>
                        </fieldset>
                    ))}
                </div>
            )}

            <div className="driver-filters__footer">
                <div className="chips">
                    {chips.length === 0 ? (
                        <span className="no-filters">لا توجد فلاتر مطبقة — يتم عرض جميع السائقين</span>
                    ) : chips.map((chip) => (
                        <span key={chip.id} className="chip">
                            {chip.text}
                            <button type="button" aria-label="إزالة" onClick={() => onApply({ ...applied, ...chip.clear })}>
                                <FiX />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="actions">
                    {(chips.length > 0 || isDirty) && (
                        <button type="button" className="clear-btn" onClick={onClear}>مسح الكل</button>
                    )}
                    <button type="submit" className="apply-btn" disabled={!isDirty}>تطبيق</button>
                </div>
            </div>
        </form>
    );
};

export default DriverFiltersPanel;
